import React from "react";
import { FiHardDrive, FiCloud, FiBriefcase, FiCheckCircle, FiEye, FiEdit3, FiX } from "react-icons/fi";
import { nazwaDlaCzlowieka, sciezkiDysku } from "./regulaNazwyDysku";
import s from "./WierszDyskuDoWniosku.module.css";

/* ════════════════════════════════════════════════════════════════════════════════════════
   WIERSZ ZASOBU W OKNIE „Utwórz nowe zgłoszenie → Zmiana uprawnień do dysków sieciowych".

   [polecenia właściciela 2026-09-13]
   R19 „Składając wniosek o dostęp do dysków, wypisuj poprawione nazwy, nazwę zasobu sieciowego bez RW -
        bo RW RO oznacza grupę która nadaje uprawnienia z AD, a także pokazuj opis."
   R20 „popraw UI, bo to jest za duże. Nie pokazuj właścicieli personalnie, pokazuj dział, ponieważ
        docelowo dyski będą mieć uprawnienia per dział nie per człowiek."

   ── DLACZEGO OSOBNY KOMPONENT, A NIE JSX W OKNIE ZGŁOSZENIA ──────────────────────────────
   Okno zgłoszenia ma 2300 linii i 42 stany; wiersz dysku to czysta funkcja danych → widok, którą
   da się zmierzyć w izolacji (co WIDAĆ: nazwa kanoniczna, ścieżka bez sufiksu, dział, opis; czego
   NIE WIDAĆ: nazwisko opiekuna). Zapadka `nazwyKanoniczneIsciezki.test.js` renderuje sam wiersz.

   ── CO WIDAĆ, A CZEGO NIE ────────────────────────────────────────────────────────────────
   • linia 1: NAZWA KANONICZNA („Analiza odpadu") + plakietka DZIAŁU (zasobu, a gdy go nie ma — działu
     podstawowego opiekuna, z dopiskiem w tytule);
   • linia 2: ŚCIEŻKA BEZ SUFIKSU RW/RO (`\\srv\PROJECT\Analiza_odpadu`) albo adres witryny + OPIS;
   • opiekuna NIE MA nigdzie (ani w treści, ani w podpowiedzi `title`) — serwer go nie wysyła (przegląd R20).
   Wysokość ≈ 2,5 rem: dwie linie drobnym pismem, bez karty, bez pustego marginesu.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Ucieczka znaków HTML — treść zgłoszenia jest składana jako HTML, a nazwy przychodzą z ewidencji. */
const ucieknij = (t) => String(t ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Dział pokazywany przy zasobie: własny dział zasobu, a gdy go nie ma — dział podstawowy opiekuna. */
export const dzialZasobu = (dysk) => {
    const nazwa = String(dysk?.departmentName || "").trim();
    if (!nazwa) return null;
    return { nazwa, zOpiekuna: Boolean(dysk?.departmentFromOwner) };
};

/** Opis dla pracownika: pole `opis` (R18), a gdy puste — dotychczasowy `description`. */
export const opisZasobu = (dysk) => String(dysk?.opis || dysk?.description || "").trim();

/** Ścieżka zasobu do pokazania: UNC bez sufiksu, adres witryny, albo sama nazwa techniczna. */
export const sciezkaZasobu = (dysk) => {
    const sciezki = sciezkiDysku(dysk);
    return sciezki.windows || sciezki.sharepoint || String(dysk?.name || "").trim();
};

/**
 * Pozycja listy w TREŚCI zgłoszenia — to samo, co widać w wierszu, plus grupa AD (po niej IT nadaje
 * dostęp) i poziom uprawnień.
 * R20 „Nie pokazuj właścicieli personalnie, pokazuj dział": treść zgłoszenia czyta także SKŁADAJĄCY,
 * więc nazwiska opiekuna w niej nie ma. Akceptującego serwer bierze z ewidencji zasobu (wnioski
 * o dostęp w `UserTicketsController`), a nie z tego napisu — obsługa nic nie traci.
 */
export const opisDyskuDoZgloszeniaHtml = (dysk, poziom) => {
    const level = poziom === "read_write" ? "Odczyt + Zapis" : "Odczyt";
    const dzial = dzialZasobu(dysk);
    const czesci = [
        sciezkaZasobu(dysk) ? `Zasób: ${ucieknij(sciezkaZasobu(dysk))}` : "",
        dysk?.name && dysk.name !== sciezkaZasobu(dysk) ? `Grupa AD: ${ucieknij(dysk.name)}` : "",
        dzial ? `Dział: ${ucieknij(dzial.nazwa)}${dzial.zOpiekuna ? " (dział opiekuna)" : ""}` : "Dział: brak",
        opisZasobu(dysk) ? ucieknij(opisZasobu(dysk)) : "",
    ].filter(Boolean);

    return `<li><strong>${ucieknij(nazwaDlaCzlowieka(dysk))}</strong> — ${level}<br/><small>${czesci.join(" · ")}</small></li>`;
};

/**
 * @param {object}   props
 * @param {object}   props.dysk        wiersz z `/my-tickets/all-drives-grouped`
 * @param {boolean}  props.zaznaczony
 * @param {string}   props.poziom      "read" | "read_write"
 * @param {Function} props.onZaznacz   (id, boolean)
 * @param {Function} props.onPoziom    (id, "read" | "read_write")
 */
const WierszDyskuDoWniosku = ({ dysk, zaznaczony, poziom = "read", onZaznacz, onPoziom }) => {
    const witryna = dysk?.kind === "sharepoint";
    const dzial = dzialZasobu(dysk);
    const opis = opisZasobu(dysk);
    const sciezka = sciezkaZasobu(dysk);
    const nazwa = nazwaDlaCzlowieka(dysk);

    // R20: „Nie pokazuj właścicieli personalnie, pokazuj dział". Opiekuna NIE MA ani w treści, ani w podpowiedzi —
    // serwer w ogóle go nie wysyła (`/my-tickets/all-drives-grouped`), a podpowiedź to też „pokazywanie".
    const podpowiedz = [
        `Grupa AD: ${dysk?.name || "—"}`,
        dzial?.zOpiekuna ? "Dział pochodzi z działu podstawowego opiekuna" : "",
    ].filter(Boolean).join("\n");

    // Klik w wiersz WYBIERA zasób; z wniosku usuwa wyłącznie jawny ✕ — tak działały karty sprzed R20.
    // Przełącznik „drugi klik odznacza” wyrzucał wybrany dysk z wniosku przy przypadkowym kliknięciu.
    const wybierz = () => {
        if (!zaznaczony) onZaznacz?.(dysk.id, true);
    };

    // Dostępność: przycisk wyboru i przyciski poziomu są RODZEŃSTWEM. Wcześniej wiersz był role=checkbox
    // z przyciskami w środku — element interaktywny w interaktywnym, który czytnik ekranu gubi.
    return (
        <div
            className={`${s.wiersz} ${zaznaczony ? s.wierszWybrany : ""}`}
            title={podpowiedz}
            data-testid="dysk-wiersz"
            data-dysk-id={dysk?.id}
        >
            <button
                type="button"
                className={s.wybor}
                onClick={wybierz}
                aria-disabled={zaznaczony ? "true" : undefined}
                data-testid="dysk-wiersz-wybor"
            >
                <span className={`${s.ikona} ${zaznaczony ? s.ikonaWybrana : ""}`} aria-hidden="true">
                    {witryna ? <FiCloud size={13} /> : <FiHardDrive size={13} />}
                </span>

                <span className={s.tresc}>
                    <span className={s.linia}>
                        <span className={s.nazwa} data-testid="dysk-wiersz-nazwa">{nazwa}</span>
                        {dzial ? (
                            <span
                                className={`${s.dzial} ${dzial.zOpiekuna ? s.dzialZOpiekuna : ""}`}
                                data-testid="dysk-wiersz-dzial"
                            >
                                <FiBriefcase size={9} aria-hidden="true" /> {dzial.nazwa}
                            </span>
                        ) : (
                            <span className={s.dzialBrak} data-testid="dysk-wiersz-dzial">bez działu</span>
                        )}
                    </span>
                    <span className={s.linia}>
                        <span className={s.sciezka} data-testid="dysk-wiersz-sciezka">{sciezka}</span>
                        {opis && <span className={s.opis} data-testid="dysk-wiersz-opis">{opis}</span>}
                    </span>
                </span>

                {!zaznaczony && (
                    <span className={s.znacznik} aria-hidden="true">
                        <FiCheckCircle size={15} className={s.znacznikIkona} />
                    </span>
                )}
            </button>

            {zaznaczony && (
                <div className={s.poziomy} role="group" aria-label={`Poziom dostępu: ${nazwa}`}>
                    <button
                        type="button"
                        className={`${s.poziom} ${poziom === "read" ? s.poziomOdczyt : ""}`}
                        onClick={() => onPoziom?.(dysk.id, "read")}
                        aria-pressed={poziom === "read"}
                        title="Tylko odczyt"
                    >
                        <FiEye size={11} aria-hidden="true" /> Odczyt
                    </button>
                    <button
                        type="button"
                        className={`${s.poziom} ${poziom === "read_write" ? s.poziomZapis : ""}`}
                        onClick={() => onPoziom?.(dysk.id, "read_write")}
                        aria-pressed={poziom === "read_write"}
                        title="Odczyt i zapis"
                    >
                        <FiEdit3 size={11} aria-hidden="true" /> Odczyt + Zapis
                    </button>
                    <button
                        type="button"
                        className={s.usun}
                        onClick={() => onZaznacz?.(dysk.id, false)}
                        aria-label={`Usuń z wniosku: ${nazwa}`}
                        title="Usuń z wniosku"
                    >
                        <FiX size={12} aria-hidden="true" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default WierszDyskuDoWniosku;
